/**
 * TaskChecklistSection - Seção Checklist da aba Resumo do modal de tarefa
 *
 * Renderiza cabeçalho "Lista de Verificação" + botão "Aplicar template" +
 * N grupos + botão para adicionar grupo. Suporta drag-and-drop dos grupos.
 *
 * @version 1.3.0
 * RELEASE: 2026-07-07 v1.0.0 - Criação inicial (Fase 1)
 * RELEASE: 2026-07-07 v1.1.0 - Fase 2: propaga comentário/menção nos itens
 * RELEASE: 2026-07-07 v1.2.0 - Drag-and-drop de grupos via @dnd-kit
 * RELEASE: 2026-07-07 v1.3.0 - Fase 3: botão Aplicar template
 */

import { useState } from 'react';
import { CheckSquare, Plus, LayoutList, ChevronDown, ChevronRight } from 'lucide-react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useTaskChecklist } from '@/hooks/useTaskChecklist';
import { ChecklistGroupCard } from './ChecklistGroupCard';
import { ChecklistApplyTemplateDialog } from './ChecklistApplyTemplateDialog';

interface TaskChecklistSectionProps {
  taskId: string;
  tenantId: string;
  readOnly?: boolean;
}

export function TaskChecklistSection({ taskId, tenantId, readOnly }: TaskChecklistSectionProps) {
  const {
    groups,
    isLoading,
    createGroup,
    updateGroup,
    deleteGroup,
    bulkToggleGroup,
    reorderGroups,
    createItem,
    updateItem,
    toggleItem,
    deleteItem,
    reorderItems,
    upsertItemComment,
    deleteItemComment,
  } = useTaskChecklist(taskId, tenantId);


  // Estado do input de criação de grupo
  const [creating, setCreating] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  // Diálogo de aplicar template (Fase 3)
  const [applyOpen, setApplyOpen] = useState(false);
  // Expandir / retrair todos os grupos
  const [allCollapsed, setAllCollapsed] = useState(false);

  // Sensores DnD para grupos (drag levemente atrasado para não conflitar com clique)
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  /** Trata fim do drag de grupo: aplica reorder otimista via RPC */
  const handleGroupDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = groups.findIndex((g) => g.id === active.id);
    const newIndex = groups.findIndex((g) => g.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    const reordered = arrayMove(groups, oldIndex, newIndex).map((g) => g.id);
    reorderGroups(reordered);
  };

  /** Submete criação do grupo se título válido */
  const submitNewGroup = async () => {
    const title = newTitle.trim();
    if (!title) { setCreating(false); return; }
    await createGroup(title);
    setNewTitle('');
    setCreating(false);
  };

  // Não renderiza nada se não há grupos e usuário não pode criar (evita ruído no modal)
  if (!isLoading && groups.length === 0 && readOnly) return null;

  const groupIds = groups.map((g) => g.id);

  return (
    <section className="space-y-3">
      {/* Cabeçalho */}
      <div className="flex items-center gap-2">
        <CheckSquare className="h-4 w-4 text-primary" />
        <h3 className="text-sm font-semibold">Lista de Verificação</h3>
        {groups.length > 0 && (
          <span className="text-xs text-muted-foreground">
            ({groups.length} {groups.length === 1 ? 'lista' : 'listas'})
          </span>
        )}
        {groups.length > 0 && (
          <button
            type="button"
            className="text-muted-foreground hover:text-foreground ml-1"
            onClick={() => setAllCollapsed((c) => !c)}
            title={allCollapsed ? 'Expandir todas as listas' : 'Recolher todas as listas'}
            aria-label={allCollapsed ? 'Expandir todas as listas' : 'Recolher todas as listas'}
          >
            {allCollapsed ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )}
          </button>
        )}
        {!readOnly && (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="ml-auto text-xs text-muted-foreground hover:text-foreground"
            onClick={() => setApplyOpen(true)}
          >
            <LayoutList className="h-3.5 w-3.5 mr-1.5" />
            Aplicar template
          </Button>
        )}
      </div>

      {/* Loading */}
      {isLoading ? (
        <div className="space-y-2">
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
        </div>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleGroupDragEnd}
        >
          <SortableContext items={groupIds} strategy={verticalListSortingStrategy}>
            <div className="space-y-3">
              {groups.map((g) => (
                <ChecklistGroupCard
                  key={g.id}
                  group={g}
                  taskId={taskId}
                  tenantId={tenantId ?? ''}
                  readOnly={readOnly}
                  collapsed={allCollapsed}
                  onRenameGroup={(title) => updateGroup({ groupId: g.id, title })}
                  onDeleteGroup={() => deleteGroup(g.id)}
                  onBulkToggle={(isDone) => bulkToggleGroup({ groupId: g.id, isDone })}
                  onCreateItem={(content) => createItem({ groupId: g.id, content })}
                  onUpdateItem={(itemId, content) => updateItem({ itemId, content })}
                  onToggleItem={(itemId, isDone) => toggleItem({ itemId, isDone })}
                  onDeleteItem={(itemId) => deleteItem(itemId)}
                  onReorderItems={(itemIds) => reorderItems({ groupId: g.id, itemIds })}
                  onUpsertItemComment={(params) => upsertItemComment(params)}
                  onDeleteItemComment={(commentId) => deleteItemComment(commentId)}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}

      {/* Adicionar nova lista */}
      {!readOnly && (
        creating ? (
          <div className="flex items-center gap-2 px-2">
            <input
              className="flex-1 bg-transparent outline-none text-sm border-b border-primary/40 pb-1"
              placeholder="Título da lista (ex.: Analisar Documentos)"
              value={newTitle}
              autoFocus
              maxLength={200}
              onChange={(e) => setNewTitle(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') { e.preventDefault(); submitNewGroup(); }
                if (e.key === 'Escape') { setNewTitle(''); setCreating(false); }
              }}
              onBlur={submitNewGroup}
            />
          </div>
        ) : (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="text-muted-foreground hover:text-foreground"
            onClick={() => setCreating(true)}
          >
            <Plus className="h-4 w-4 mr-2" />
            Adicionar uma lista de verificação
          </Button>
        )
      )}

      {/* Diálogo Aplicar Template (Fase 3) */}
      <ChecklistApplyTemplateDialog
        open={applyOpen}
        onOpenChange={setApplyOpen}
        taskId={taskId}
        hasExistingChecklist={groups.length > 0}
      />
    </section>
  );
}
